import { getJourneyFixture } from "../fixtures/journey";
import type { JourneyType } from "../types/journey";
import { JOURNEY_TYPES } from "../types/journey";
import type {
	JourneyFetchResult,
	JourneyS3File,
} from "../types/journeyS3Schema";
import {
	buildCloudFrontUrl,
	JOURNEY_S3_SCHEMA_VERSION,
} from "../types/journeyS3Schema";

/**
 * Get the journey for a given treatment type and locale.
 * Fetches from CloudFront CDN when VITE_JOURNEY_CDN_BASE_URL is set,
 * falling back silently to bundled fixtures on any CDN failure.
 * Uses native fetch() — see D008 for why the api client is not used here.
 */
export async function getJourney(
	type: JourneyType,
	locale = "en",
): Promise<JourneyFetchResult> {
	const cdnBase = import.meta.env.VITE_JOURNEY_CDN_BASE_URL;

	if (cdnBase) {
		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), 5000);
		try {
			const url = buildCloudFrontUrl(type, locale);
			const response = await fetch(url, {
				signal: controller.signal,
			});

			if (!response.ok) {
				throw new Error(`HTTP ${response.status}`);
			}

			const data = (await response.json()) as JourneyS3File;

			if (data.schemaVersion !== JOURNEY_S3_SCHEMA_VERSION) {
				throw new Error(
					`Schema version mismatch: expected ${JOURNEY_S3_SCHEMA_VERSION}, got ${data.schemaVersion}`,
				);
			}

			return { journey: data.journey, source: "cdn" };
		} catch (error) {
			console.warn("CloudFront fetch failed for journey", {
				type,
				locale,
				error,
			});
		} finally {
			clearTimeout(timeoutId);
		}
	}

	const journey = getJourneyFixture(type, locale);
	if (!journey) {
		throw new Error(`Unknown journey type: ${type}`);
	}
	return { journey: structuredClone(journey), source: "fallback" };
}

/**
 * Get the list of all available journey types.
 * Future: backed by `GET /api/journey-types`.
 */
export async function getJourneyTypes(): Promise<JourneyType[]> {
	return [...JOURNEY_TYPES];
}
